// src/AdminResendPanel.js
import React, { useState, useEffect } from 'react';
import { authedFetch } from './utils/authedFetch';
import { apiUrl } from './utils/apiBase';

const AdminResendPanel = () => {
 const [email, setEmail] = useState('');
 const [resending, setResending] = useState(false);
 const [result, setResult] = useState(null);
 const [error, setError] = useState(null);
 const [reassignEmail, setReassignEmail] = useState('');
 const [reassignLoading, setReassignLoading] = useState(false);
 const [reassignResult, setReassignResult] = useState(null);
 const [reassignError, setReassignError] = useState(null);
 const [testModeEnabled, setTestModeEnabled] = useState(false);
 const [testModeLoading, setTestModeLoading] = useState(false);
const [extendDrawDate, setExtendDrawDate] = useState('');
 const [additionalNumbers, setAdditionalNumbers] = useState('');
 const [lotteryType, setLotteryType] = useState('powerball');
 const [extendLoading, setExtendLoading] = useState(false);
 const [extendResult, setExtendResult] = useState(null);
 const [extendError, setExtendError] = useState(null);
const [extendStatus, setExtendStatus] = useState(null);

 useEffect(() => {
   const checkTestMode = async () => {
     try {
       const response = await authedFetch(apiUrl('getSystemConfig'), {
         method: 'POST',
         headers: { 'Content-Type': 'application/json' },
         body: JSON.stringify({ configKey: 'test_mode' })
       });
       const data = await response.json();
       setTestModeEnabled(data.enabled === true);
     } catch (error) {
       console.error('Error checking test mode:', error);
     }
   };
   checkTestMode();
 }, []);

 const toggleTestMode = async () => {
   setTestModeLoading(true);
   try {
     const response = await authedFetch(apiUrl('setSystemConfig'), {
       method: 'POST',
       headers: { 'Content-Type': 'application/json' },
       body: JSON.stringify({
         configKey: 'test_mode',
         configValue: {
           enabled: !testModeEnabled,
           toggledBy: 'admin@lottologic.ai',
           toggledAt: new Date().toISOString()
         }
       })
     });
     
     if (!response.ok) throw new Error('Failed to toggle test mode');
     
     setTestModeEnabled(!testModeEnabled);
     alert(`Test mode ${!testModeEnabled ? 'ENABLED' : 'DISABLED'}`);
   } catch (error) {
     alert('Error toggling test mode: ' + error.message);
   }
   setTestModeLoading(false);
 };

  const handleResendNumbers = async () => {
    if (!email.trim()) {
      setError('Please enter a valid email address');
      return;
    }

    setResending(true);
    setError(null);
    setResult(null);
    
    try {
      const response = await authedFetch(apiUrl('resendLatestNumbers'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email.trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend numbers');
      }

      setResult(data);
      setEmail('');
      
    } catch (error) {
      console.error('Failed to resend numbers:', error);
      setError(error.message);
    } finally {
      setResending(false);
    }
  };

  const handleReassignNumbers = async () => {
    if (!reassignEmail.trim()) {
      setReassignError('Please enter a valid email address');
      return;
    }

    setReassignLoading(true);
    setReassignError(null);
    setReassignResult(null);
    
    try {
      const response = await authedFetch(apiUrl('reassignMemberNumbers'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          memberEmail: reassignEmail.trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reassign numbers');
      }

      setReassignResult({ ...data, memberEmail: reassignEmail.trim() });
      setReassignEmail('');
      
    } catch (error) {
      console.error('Failed to reassign numbers:', error);
      setReassignError(error.message);
    } finally {
      setReassignLoading(false);
    }
  };

  const handleExtendRollout = async () => {
    if (!extendDrawDate.trim() || !additionalNumbers) {
      setExtendError('Please enter both draw date and number of additional numbers');
      return;
    }

setExtendLoading(true);
setExtendStatus(`Rollout extension in progress... Adding ${parseInt(additionalNumbers).toLocaleString()} numbers to ${extendDrawDate} ${lotteryType === 'powerball' ? 'Powerball' : 'Mega Millions'} rollout`);
    setExtendError(null);
    setExtendResult(null);
    
    try {
      const endpoint = lotteryType === 'powerball' 
        ? 'extendPowerballRollout' 
        : 'extendMegaMillionsRollout';
      
      const response = await authedFetch(apiUrl(endpoint), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          drawDate: extendDrawDate.trim(),
          additionalNumbers: parseInt(additionalNumbers),
          updatedBy: 'admin@lottologic.ai'
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to extend rollout');
      }

      setExtendResult(data);
      setExtendDrawDate('');
      setAdditionalNumbers('');
      
    } catch (error) {
      console.error('Failed to extend rollout:', error);
      setExtendError(error.message);
    } finally {
      setExtendLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleResendNumbers();
    }
  };

  return (
    <div style={containerStyle}>

{/* Test Mode Toggle */}
      <div style={{
        ...formCardStyle,
        border: testModeEnabled ? '3px solid #dc2626' : '1px solid #e5e7eb',
        backgroundColor: testModeEnabled ? '#fef2f2' : 'white',
        marginBottom: '2rem'
      }}>
        <div style={headerStyle}>
          <h3 style={{color: testModeEnabled ? '#dc2626' : '#333'}}>
            Pipeline Test Mode
          </h3>
          <p style={{
            ...descriptionStyle,
            color: testModeEnabled ? '#dc2626' : '#666',
            fontWeight: testModeEnabled ? '600' : 'normal'
          }}>
            Status: <strong>{testModeEnabled ? 'TEST MODE ACTIVE' : 'PRODUCTION MODE'}</strong>
          </p>
        </div>
        
        <button
          onClick={toggleTestMode}
          disabled={testModeLoading}
          style={{
            ...buttonStyle,
            backgroundColor: testModeLoading ? '#ccc' : (testModeEnabled ? '#dc2626' : '#22c55e'),
            cursor: testModeLoading ? 'not-allowed' : 'pointer',
            marginBottom: '1rem'
          }}
        >
          {testModeLoading ? 'Updating...' : (testModeEnabled ? 'DISABLE Test Mode' : 'ENABLE Test Mode')}
        </button>
        
        {testModeEnabled && (
          <div style={{
            backgroundColor: '#fef2f2',
            padding: '1rem',
            borderRadius: '6px',
            border: '1px solid #fecaca'
          }}>
            <strong style={{color: '#dc2626'}}>Test Mode Instructions:</strong>
            <ul style={{color: '#dc2626', marginTop: '0.5rem', paddingLeft: '1.5rem'}}>
              <li>Use date format: <code>_TEST_2025-09-05</code></li>
              <li>All data will be written to test collections</li>
              <li>No emails or SMS will be sent</li>
              <li>Remember to disable when finished testing</li>
            </ul>
          </div>
        )}
      </div>

      <div style={headerStyle}>
        <h2>Resend Member Numbers</h2>
        <p style={descriptionStyle}>
          Enter a member's email address to resend their latest lottery numbers via email and SMS.
        </p>
      </div>

      <div style={formCardStyle}>
        <div style={inputGroupStyle}>
          <label style={labelStyle}>Member Email Address</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="member@example.com"
            style={inputStyle}
            disabled={resending}
          />
        </div>

        <button
          onClick={handleResendNumbers}
          disabled={resending || !email.trim()}
          style={{
            ...buttonStyle,
            backgroundColor: resending || !email.trim() ? '#ccc' : '#1976d2',
            cursor: resending || !email.trim() ? 'not-allowed' : 'pointer'
          }}
        >
          {resending ? 'Sending...' : 'Resend Latest Numbers'}
        </button>
      </div>

      {/* Error Display */}
      {error && (
        <div style={errorCardStyle}>
          <div style={errorHeaderStyle}>
            <span style={errorIconStyle}>⌘</span>
            <strong>Error</strong>
          </div>
          <p style={errorMessageStyle}>{error}</p>
        </div>
      )}

      {/* Success Display */}
      {result && (
        <div style={successCardStyle}>
          <div style={successHeaderStyle}>
            <span style={successIconStyle}>✅</span>
            <strong>Numbers Resent Successfully</strong>
          </div>
          <p style={successMessageStyle}>
            Latest lottery numbers have been resent to <strong>{result.memberEmail || email}</strong>
          </p>
          
          <div style={resultsGridStyle}>
            <div style={resultItemStyle}>
              <div style={gameLabelStyle}>Powerball</div>
              <div style={resultStatusStyle}>
                {result.results?.powerball?.success ? (
                  <span style={successTextStyle}>
                    ✓ Sent ({result.results.powerball.numbersCount} numbers)
                  </span>
                ) : (
                  <span style={errorTextStyle}>
                    ✗ Failed: {result.results?.powerball?.error || 'Unknown error'}
                  </span>
                )}
              </div>
              {result.results?.powerball?.drawDate && (
                <div style={drawDateStyle}>
                  Draw Date: {result.results.powerball.drawDate}
                </div>
              )}
            </div>

            <div style={resultItemStyle}>
              <div style={gameLabelStyle}>Mega Millions</div>
              <div style={resultStatusStyle}>
                {result.results?.megamillions?.success ? (
                  <span style={successTextStyle}>
                    ✓ Sent ({result.results.megamillions.numbersCount} numbers)
                  </span>
                ) : (
                  <span style={errorTextStyle}>
                    ✗ Failed: {result.results?.megamillions?.error || 'Unknown error'}
                  </span>
                )}
              </div>
              {result.results?.megamillions?.drawDate && (
                <div style={drawDateStyle}>
                  Draw Date: {result.results.megamillions.drawDate}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div style={infoCardStyle}>
        <h4 style={infoHeaderStyle}>How It Works</h4>
        <ul style={infoListStyle}>
          <li>Enter the member's email address exactly as it appears in their account</li>
          <li>System will find their most recent Powerball and Mega Millions numbers</li>
          <li>Numbers will be sent via email and SMS (if SMS is enabled for the member)</li>
          <li>Member's account must be in "active" status for resend to work</li>
          <li>If no numbers exist for a game, that game will be skipped</li>
        </ul>
      </div>

      {/* Reassign Numbers Section */}
      <div style={{...formCardStyle, marginTop: '3rem'}}>
        <div style={headerStyle}>
          <h2>Reassign Latest Numbers</h2>
          <p style={descriptionStyle}>
            Generate and assign new numbers for the upcoming draws. This will overwrite any existing assignments and send new numbers via email and SMS.
          </p>
        </div>

        <div style={inputGroupStyle}>
          <label style={labelStyle}>Member Email Address</label>
          <input
            type="email"
            value={reassignEmail}
            onChange={(e) => setReassignEmail(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleReassignNumbers()}
            placeholder="member@example.com"
            style={inputStyle}
            disabled={reassignLoading}
          />
        </div>

        <button
          onClick={handleReassignNumbers}
          disabled={reassignLoading || !reassignEmail.trim()}
          style={{
            ...buttonStyle,
            backgroundColor: reassignLoading || !reassignEmail.trim() ? '#ccc' : '#e67e22',
            cursor: reassignLoading || !reassignEmail.trim() ? 'not-allowed' : 'pointer'
          }}
        >
          {reassignLoading ? 'Reassigning Numbers...' : 'Reassign Latest Numbers'}
        </button>
      </div>

      {/* Reassign Error Display */}
      {reassignError && (
        <div style={errorCardStyle}>
          <div style={errorHeaderStyle}>
            <span style={errorIconStyle}>⌘</span>
            <strong>Error</strong>
          </div>
          <p style={errorMessageStyle}>{reassignError}</p>
        </div>
      )}

      {/* Reassign Success Display */}
      {reassignResult && (
        <div style={successCardStyle}>
          <div style={successHeaderStyle}>
            <span style={successIconStyle}>✅</span>
            <strong>Numbers Reassigned Successfully</strong>
          </div>
          <p style={successMessageStyle}>
            New lottery numbers have been assigned and sent to <strong>{reassignResult.memberEmail}</strong>
          </p>
          
          <div style={resultsGridStyle}>
            <div style={resultItemStyle}>
              <div style={gameLabelStyle}>Powerball</div>
              <div style={resultStatusStyle}>
                {reassignResult.results?.powerball?.success ? (
                  <span style={successTextStyle}>
                    ✓ Assigned ({reassignResult.results.powerball.numbersAssigned} numbers)
                  </span>
                ) : (
                  <span style={errorTextStyle}>
                    ✗ Failed: {reassignResult.results?.powerball?.error || 'Unknown error'}
                  </span>
                )}
              </div>
              {reassignResult.results?.powerball?.drawId && (
                <div style={drawDateStyle}>
                  Draw Date: {reassignResult.results.powerball.drawId}
                </div>
              )}
            </div>

            <div style={resultItemStyle}>
              <div style={gameLabelStyle}>Mega Millions</div>
              <div style={resultStatusStyle}>
                {reassignResult.results?.megamillions?.success ? (
                  <span style={successTextStyle}>
                    ✓ Assigned ({reassignResult.results.megamillions.numbersAssigned} numbers)
                  </span>
                ) : (
                  <span style={errorTextStyle}>
                    ✗ Failed: {reassignResult.results?.megamillions?.error || 'Unknown error'}
                  </span>
                )}
              </div>
              {reassignResult.results?.megamillions?.drawId && (
                <div style={drawDateStyle}>
                  Draw Date: {reassignResult.results.megamillions.drawId}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

{/* Extend Rollout Section */}
      <div style={{...formCardStyle, marginTop: '3rem'}}>
        <div style={headerStyle}>
          <h2>Extend Rollout Numbers</h2>
          <p style={descriptionStyle}>
            Add additional number sets to an existing completed rollout without affecting already-assigned numbers.
          </p>
        </div>

        <div style={inputGroupStyle}>
          <label style={labelStyle}>Draw Date (YYYY-MM-DD)</label>
          <input
            type="text"
            value={extendDrawDate}
            onChange={(e) => setExtendDrawDate(e.target.value)}
            placeholder="2025-09-05"
            style={inputStyle}
            disabled={extendLoading}
          />
        </div>

        <div style={inputGroupStyle}>
          <label style={labelStyle}>Additional Numbers to Generate</label>
          <input
            type="number"
            value={additionalNumbers}
            onChange={(e) => setAdditionalNumbers(e.target.value)}
            placeholder="50000"
            min="1000"
            style={inputStyle}
            disabled={extendLoading}
          />
        </div>

        <div style={inputGroupStyle}>
          <label style={labelStyle}>Lottery Type</label>
          <select
            value={lotteryType}
            onChange={(e) => setLotteryType(e.target.value)}
            style={inputStyle}
            disabled={extendLoading}
          >
            <option value="powerball">Powerball</option>
            <option value="megamillions">Mega Millions</option>
          </select>
        </div>

        <button
          onClick={handleExtendRollout}
          disabled={extendLoading || !extendDrawDate.trim() || !additionalNumbers}
          style={{
            ...buttonStyle,
            backgroundColor: extendLoading || !extendDrawDate.trim() || !additionalNumbers ? '#ccc' : '#8b5cf6',
            cursor: extendLoading || !extendDrawDate.trim() || !additionalNumbers ? 'not-allowed' : 'pointer'
          }}
        >
          {extendLoading ? 'Extending Rollout...' : 'Extend Rollout'}
        </button>

      {/* Status Display */}
      {extendStatus && (
        <div style={{
          backgroundColor: '#e0f2fe',
          border: '1px solid #81d4fa',
          borderRadius: '8px',
          padding: '1rem',
          marginTop: '1rem',
          color: '#0277bd',
          textAlign: 'center'
        }}>
          <strong>{extendStatus}</strong>
        </div>
      )}
      </div>

      {/* Extend Rollout Error Display */}
      {extendError && (
        <div style={errorCardStyle}>
          <div style={errorHeaderStyle}>
            <span style={errorIconStyle}>⚠</span>
            <strong>Error</strong>
          </div>
          <p style={errorMessageStyle}>{extendError}</p>
        </div>
      )}

      {/* Extend Rollout Success Display */}
      {extendResult && (
        <div style={successCardStyle}>
          <div style={successHeaderStyle}>
            <span style={successIconStyle}>✅</span>
            <strong>Rollout Extended Successfully</strong>
          </div>
          <p style={successMessageStyle}>
            Added <strong>{extendResult.additionalGenerated?.toLocaleString()}</strong> numbers to {extendResult.drawDate} rollout
          </p>
          <div style={resultItemStyle}>
            <div style={gameLabelStyle}>Summary</div>
            <div style={resultStatusStyle}>
              <span style={successTextStyle}>
                New Total: {extendResult.newTotal?.toLocaleString()} numbers
              </span>
            </div>
            <div style={drawDateStyle}>
              Duration: {Math.round(extendResult.duration || 0)} seconds
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Styles
const containerStyle = {
  padding: '2rem',
  maxWidth: '800px',
  margin: '0 auto'
};

const headerStyle = {
  marginBottom: '2rem',
  textAlign: 'center'
};

const descriptionStyle = {
  color: '#666',
  fontSize: '1.1rem',
  marginTop: '0.5rem'
};

const formCardStyle = {
  backgroundColor: 'white',
  padding: '2rem',
  borderRadius: '10px',
  boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  marginBottom: '2rem'
};

const inputGroupStyle = {
  marginBottom: '1.5rem'
};

const labelStyle = {
  display: 'block',
  fontSize: '1rem',
  fontWeight: '600',
  marginBottom: '0.5rem',
  color: '#333'
};

const inputStyle = {
  width: '100%',
  padding: '0.75rem',
  fontSize: '1rem',
  border: '2px solid #e5e7eb',
  borderRadius: '8px',
  boxSizing: 'border-box',
  transition: 'border-color 0.2s'
};

const buttonStyle = {
  width: '100%',
  padding: '0.875rem',
  fontSize: '1rem',
  fontWeight: '600',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  transition: 'background-color 0.2s'
};

const errorCardStyle = {
  backgroundColor: '#fef2f2',
  border: '1px solid #fecaca',
  borderRadius: '8px',
  padding: '1rem',
  marginBottom: '1rem'
};

const errorHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  marginBottom: '0.5rem',
  color: '#dc2626'
};

const errorIconStyle = {
  fontSize: '1.2rem'
};

const errorMessageStyle = {
  color: '#dc2626',
  margin: 0
};

const successCardStyle = {
  backgroundColor: '#f0f9ff',
  border: '1px solid #bfdbfe',
  borderRadius: '8px',
  padding: '1.5rem',
  marginBottom: '2rem'
};

const successHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  marginBottom: '1rem',
  color: '#1976d2'
};

const successIconStyle = {
  fontSize: '1.2rem'
};

const successMessageStyle = {
  color: '#1976d2',
  marginBottom: '1rem'
};

const resultsGridStyle = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '1rem'
};

const resultItemStyle = {
  backgroundColor: 'white',
  padding: '1rem',
  borderRadius: '6px',
  border: '1px solid #e5e7eb'
};

const gameLabelStyle = {
  fontWeight: '600',
  marginBottom: '0.5rem'
};

const resultStatusStyle = {
  marginBottom: '0.5rem'
};

const successTextStyle = {
  color: '#22c55e',
  fontSize: '0.9rem'
};

const errorTextStyle = {
  color: '#ef4444',
  fontSize: '0.9rem'
};

const drawDateStyle = {
  fontSize: '0.8rem',
  color: '#666'
};

const infoCardStyle = {
  backgroundColor: '#f8f9fa',
  padding: '1.5rem',
  borderRadius: '8px',
  border: '1px solid #e5e7eb'
};

const infoHeaderStyle = {
  margin: '0 0 1rem 0',
  color: '#333'
};

const infoListStyle = {
  margin: 0,
  paddingLeft: '1.5rem',
  color: '#666'
};

export default AdminResendPanel;